import { create } from 'zustand';
import type { PulseContentItem, PulseEvent, PulseStats, TipPopup } from '../types';
import {
  DELETED_LINGER_MS,
  MAX_GRID_CELLS,
  TIP_POPUP_DURATION_MS,
} from '../constants';

interface PulseState {
  /** All content items currently in the grid. */
  items: PulseContentItem[];
  /** Recently uploaded items (chronological, newest first) — shown in bottom ticker row. */
  recentItems: PulseContentItem[];
  /** Active tip popups (ephemeral, auto-removed). */
  tipPopups: TipPopup[];
  /** Platform stats for header/footer. */
  stats: PulseStats;
  /** Process an incoming SSE event. */
  processEvent: (event: PulseEvent) => void;
  /** Set initial grid state from REST endpoint. */
  setInitialGrid: (items: PulseContentItem[], stats: PulseStats) => void;
  /** Remove expired tip popups. */
  cleanupPopups: () => void;
  /** Remove items that have lingered in deleted state. */
  cleanupDeleted: () => void;
}

const EMPTY_STATS: PulseStats = {
  activeUsers: 0,
  uploadsPerMinute: 0,
  tipsToday: 0,
  tipsTodayAmount: 0,
  tipsTodayCurrency: 'AUD',
  gmv24h: 0,
  gmv24hCurrency: 'AUD',
  activeCreators: 0,
  contentUploadedToday: 0,
  moderationQueueDepth: 0,
};

export const usePulseStore = create<PulseState>((set, get) => ({
  items: [],
  recentItems: [],
  tipPopups: [],
  stats: EMPTY_STATS,

  setInitialGrid: (items, stats) => {
    // Recent row: show newest items sorted by upload time.
    const recent = [...items]
      .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
      .slice(0, 20);
    set({ items: items.slice(0, MAX_GRID_CELLS), recentItems: recent, stats });
  },

  processEvent: (event) => {
    const state = get();

    switch (event.type) {
      case 'content.uploaded': {
        const incoming: PulseContentItem = {
          contentId: event.contentId,
          thumbnailUrl: event.thumbnailUrl,
          animatedPreviewUrl: event.animatedPreviewUrl,
          creatorUsername: event.creatorUsername,
          creatorAvatarUrl: event.creatorAvatarUrl,
          state: 'new',
          monetized: false,
          rankScore: 100, // high initial score for visibility
          uploadedAt: event.timestamp,
          tipsTotal: 0,
          viewsRecent: 0,
          velocity: 0,
          moderationCleared: false,
        };
        const upsert = (list: PulseContentItem[], cap: number) => {
          const idx = list.findIndex((i) => i.contentId === event.contentId);
          if (idx >= 0) {
            const existing = list[idx];
            if (!existing) return list;
            const next = [...list];
            next[idx] = { ...existing, ...incoming, rankScore: existing.rankScore };
            return next;
          }
          return [incoming, ...list].slice(0, cap);
        };
        set({
          items: upsert(state.items, MAX_GRID_CELLS),
          recentItems: upsert(state.recentItems, 20),
        });
        break;
      }

      case 'content.published': {
        set({
          items: state.items.map((item) =>
            item.contentId === event.contentId
              ? { ...item, state: 'active' as const, moderationCleared: true }
              : item,
          ),
        });
        break;
      }

      case 'content.moderated': {
        set({
          items: state.items.map((item) =>
            item.contentId === event.contentId
              ? { ...item, state: 'moderated' as const }
              : item,
          ),
        });
        // Schedule removal after linger period.
        setTimeout(() => {
          const current = get();
          set({
            items: current.items.filter((i) => !(i.contentId === event.contentId && i.state === 'moderated')),
          });
        }, DELETED_LINGER_MS * 2);
        break;
      }

      case 'content.deleted': {
        set({
          items: state.items.map((item) =>
            item.contentId === event.contentId
              ? { ...item, state: 'deleted' as const }
              : item,
          ),
        });
        // Remove from grid after linger.
        setTimeout(() => {
          const current = get();
          set({
            items: current.items.filter((i) => i.contentId !== event.contentId),
          });
        }, DELETED_LINGER_MS);
        break;
      }

      case 'content.monetized': {
        set({
          items: state.items.map((item) =>
            item.contentId === event.contentId ? { ...item, monetized: true } : item,
          ),
        });
        break;
      }

      case 'content.trending': {
        set({
          items: state.items.map((item) =>
            item.contentId === event.contentId
              ? { ...item, state: 'trending' as const, velocity: event.velocity }
              : item,
          ),
        });
        break;
      }

      case 'tip.received': {
        // Update item tip total.
        const updatedItems = state.items.map((item) =>
          item.contentId === event.contentId
            ? { ...item, tipsTotal: item.tipsTotal + event.amount, monetized: true }
            : item,
        );
        // Add tip popup.
        const popup: TipPopup = {
          contentId: event.contentId,
          amount: event.amount,
          currency: event.currency,
          tipperUsername: event.tipperUsername,
          createdAt: Date.now(),
        };
        set({
          items: updatedItems,
          tipPopups: [...state.tipPopups, popup],
        });
        // Auto-remove popup.
        setTimeout(() => {
          const current = get();
          set({
            tipPopups: current.tipPopups.filter((p) => p !== popup),
          });
        }, TIP_POPUP_DURATION_MS);
        break;
      }

      case 'ranking.updated': {
        const rankMap = new Map(event.rankings.map((r) => [r.contentId, r.rankScore]));
        set({
          items: state.items
            .map((item) => ({
              ...item,
              rankScore: rankMap.get(item.contentId) ?? item.rankScore * 0.95,
            }))
            .sort((a, b) => b.rankScore - a.rankScore),
        });
        break;
      }

      case 'stats.updated': {
        set({ stats: event.stats });
        break;
      }
    }
  },

  cleanupPopups: () => {
    const now = Date.now();
    set((state) => ({
      tipPopups: state.tipPopups.filter((p) => now - p.createdAt < TIP_POPUP_DURATION_MS),
    }));
  },

  cleanupDeleted: () => {
    set((state) => ({
      items: state.items.filter((i) => i.state !== 'deleted'),
    }));
  },
}));
